import { LOB_PERSONAL_AUTO } from 'variables/constants';
import { parseDateString } from 'utils/helpers';

export const LOB_AUTO = 'auto';
export const LOB_PROPERTY = 'property';

export const WARNING_CODE_EFFECTIVE_DATE_IN_PAST = 'EFFECTIVE_DATE_IN_PAST';
export const WARNING_CODE_EFFECTIVE_DATE_OUTDATED = 'EFFECTIVE_DATE_OUTDATED';

export const RISK_DETAILS_CAPTURING_TOOLTIP =
  'Capturing risk details in Acturis';

export const isEffectiveDateWarningCode = (code?: string | null): boolean =>
  code === WARNING_CODE_EFFECTIVE_DATE_OUTDATED ||
  code === WARNING_CODE_EFFECTIVE_DATE_IN_PAST;

export type QuoteLOB = typeof LOB_AUTO | typeof LOB_PROPERTY;

export interface QuoteNavigationState {
  isValidForRfq: boolean;
  hasCarrierSelected: boolean;
  validationErrorCount: number;
  discrepancyPending: boolean;
  rfqInProgress: boolean;
  effectiveDateUnsupported?: boolean;
}

const EFFECTIVE_DATE_TOOLTIP =
  'Set a supported effective date on Policy Information before continuing';

export interface QuoteStep {
  key: string;
  label: string;
  isDisabled?: (state: QuoteNavigationState) => boolean;
  disabledTooltip?: (state: QuoteNavigationState) => string;
}

const rfqInProgressDisable = {
  isDisabled: (state: QuoteNavigationState) => state.rfqInProgress,
  disabledTooltip: () => 'Cannot edit while quotes are being fetched',
};

const AUTO_BASE_STEPS: QuoteStep[] = [
  { key: 'policy-info', label: 'Policy Information', ...rfqInProgressDisable },
  { key: 'vehicle', label: 'Vehicle', ...rfqInProgressDisable },
  { key: 'drivers', label: 'Drivers', ...rfqInProgressDisable },
  { key: 'assignment', label: 'Assignment', ...rfqInProgressDisable },
];

const PROPERTY_BASE_STEPS: QuoteStep[] = [
  { key: 'policy-info', label: 'Policy Information', ...rfqInProgressDisable },
  { key: 'locations', label: 'Location(s)', ...rfqInProgressDisable },
  { key: 'general', label: 'General', ...rfqInProgressDisable },
  { key: 'additional', label: 'Additional', ...rfqInProgressDisable },
];

const CARRIERS_STEP: QuoteStep = {
  key: 'carriers',
  label: 'Carriers',
};

const VALIDATIONS_STEP: QuoteStep = {
  key: 'validations',
  label: 'Validations',
  isDisabled: (state) =>
    state.rfqInProgress ||
    !state.hasCarrierSelected ||
    state.discrepancyPending,
  disabledTooltip: (state) =>
    state.rfqInProgress
      ? 'Cannot view while quotes are being fetched'
      : state.discrepancyPending
        ? 'Complete driver reports before proceeding'
        : 'Select a carrier quote to view validations',
};

const QUOTE_STEP: QuoteStep = {
  key: 'quote',
  label: 'Quote',
  isDisabled: (state) =>
    state.rfqInProgress ||
    !state.hasCarrierSelected ||
    state.discrepancyPending,
  disabledTooltip: (state) =>
    state.rfqInProgress
      ? 'Cannot view while quotes are being fetched'
      : state.discrepancyPending
        ? 'Complete driver reports before proceeding'
        : 'Select a carrier quote to view results',
};

const PROPERTY_CARRIERS_STEP: QuoteStep = CARRIERS_STEP;

const PROPERTY_VALIDATIONS_STEP: QuoteStep = VALIDATIONS_STEP;

const PROPERTY_QUOTE_STEP: QuoteStep = QUOTE_STEP;

const blockOnUnsupportedDate = (step: QuoteStep): QuoteStep => {
  if (step.key === 'policy-info') return step;
  return {
    ...step,
    isDisabled: (state) =>
      Boolean(state.effectiveDateUnsupported) ||
      (step.isDisabled?.(state) ?? false),
    disabledTooltip: (state) =>
      state.effectiveDateUnsupported
        ? EFFECTIVE_DATE_TOOLTIP
        : (step.disabledTooltip?.(state) ?? ''),
  };
};

const AUTO_STEPS: QuoteStep[] = [
  ...AUTO_BASE_STEPS,
  CARRIERS_STEP,
  VALIDATIONS_STEP,
  QUOTE_STEP,
].map(blockOnUnsupportedDate);

const PROPERTY_STEPS: QuoteStep[] = [
  ...PROPERTY_BASE_STEPS,
  PROPERTY_CARRIERS_STEP,
  PROPERTY_VALIDATIONS_STEP,
  PROPERTY_QUOTE_STEP,
].map(blockOnUnsupportedDate);

export const getStepsForLOB = (lob: QuoteLOB): QuoteStep[] => {
  return lob === LOB_AUTO ? AUTO_STEPS : PROPERTY_STEPS;
};

export const STEP_LABELS: Record<string, string> = [
  ...AUTO_BASE_STEPS,
  ...PROPERTY_BASE_STEPS,
  CARRIERS_STEP,
  VALIDATIONS_STEP,
  QUOTE_STEP,
].reduce(
  (acc, step) => {
    acc[step.key] = step.label;
    return acc;
  },
  {} as Record<string, string>,
);

export const getDefaultStep = (lob: QuoteLOB): string => {
  const steps = getStepsForLOB(lob);
  return steps[0].key;
};

export const COMPANY_NAME = 'Billyard Insurance Group Inc.';

export const getLobFromLineOfBusiness = (lineOfBusiness: number): QuoteLOB => {
  return lineOfBusiness === LOB_PERSONAL_AUTO ? LOB_AUTO : LOB_PROPERTY;
};

export const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  const date = parseDateString(dateString) ?? new Date(dateString);
  return date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatCurrency = (value: string | null | undefined): string => {
  if (!value) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return '-';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

export const formatCurrencyNumber = (
  value: number | null | undefined,
): string => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatCoverageCode = (code: string | null | undefined): string => {
  if (!code) return '-';
  const num = parseFloat(code);
  if (isNaN(num)) return code;
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

export const formatCoverageLabel = (
  code: string | null | undefined,
  getLabel: (code: string, field: string) => string,
  field: string,
  fallback: string = '-',
  suffix: string = '',
): string => {
  if (!code) return fallback;
  const label = getLabel(code, field);
  if (label && label !== code) return label + suffix;
  return formatCoverageCode(code) + suffix;
};

export const CONSENT_METHOD_VERBAL = 1;
export const CONSENT_METHOD_WRITTEN = 2;

export const CONSENT_TYPE_DASH_HISTORY = 1;
export const CONSENT_TYPE_ALL_COVERAGES_DECLINE = 2;
export const CONSENT_TYPE_PERSONAL_INFO_AUTH = 3;
export const CONSENT_TYPE_CREDIT_CHECK = 4;
export const CONSENT_TYPE_ACCIDENT_BENEFITS_DECLINE = 5;
export const CONSENT_TYPE_LIABILITY_LIMIT_REDUCTION = 7;
export const CONSENT_TYPE_DCPD_DECLINE = 8;
export const CONSENT_TYPE_PHYSICAL_DAMAGE_DECLINE = 9;
export const CONSENT_TYPE_OPCF_20_DECLINE = 10;
export const CONSENT_TYPE_OPCF_27_DECLINE = 11;
export const CONSENT_TYPE_ACCIDENT_WAIVER_DECLINE = 12;
export const CONSENT_TYPE_MINOR_CONVICTION_DECLINE = 13;
export const CONSENT_TYPE_OPCF_43_DECLINE = 14;
export const CONSENT_TYPE_HAB_LIABILITY_LIMIT_REDUCTION = 15;
export const CONSENT_TYPE_HAB_LIABILITY_UNDERWRITING_APPROVAL = 16;
export const CONSENT_TYPE_OPCF_44_DECLINE = 17;

interface TaxInfo {
  amount: number;
  percentage_amount?: number;
}

export interface TaxCalculation {
  taxTotal: number;
  taxPercentage: number;
}

export const calculateTaxes = (
  taxes: TaxInfo[] | null | undefined,
): TaxCalculation => {
  const taxTotal = taxes?.reduce((sum, tax) => sum + (tax.amount || 0), 0) || 0;
  const taxPercentage = taxes?.[0]?.percentage_amount || 0;
  return { taxTotal, taxPercentage };
};
